import { useCallback, useMemo, useState } from "react";
import { Phone, Mail, Plus, Users, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useNavigate } from "react-router";
import { formatMoney } from "@/lib/money";
import { cn, getInitials } from "@/lib/utils";
import { matchesTextQuery } from "@/lib/textSearch";
import { HoverPopover, Button, ToggleGroup, brandFillClass } from "@/components/ui";
import { PageFrame, ListToolbar, buildListResetToken } from "@/components/common";
import type {
  AdminUserListItem,
  AdminUserListLabels,
  AdminUserRole,
} from "./types";
import { ROLE_ICON_CONFIG } from "./roleIconConfig";
import { AdminStatusFilter } from "@/components/admin/AdminStatusFilter";
import {
  AdminRegionFilter,
  type AdminRegionFilterValue,
} from "@/components/admin/AdminRegionFilter";
import { AdminInactiveStatusDot } from "@/components/admin/AdminInactiveStatusDot";
import {
  countByStatusFilter,
  matchesStatusFilter,
  type StatusFilter,
} from "@/components/admin/adminListStatus";
import {
  DataTable,
  DataTablePrimaryText,
  createActionsColumn,
  type DataTableColumn,
} from "@/components/table";

type RoleFilter = "all" | AdminUserRole;
type SortColumn = "name" | "contact" | "balance" | "relevant";

const RELEVANT_COUNT_KEY: Record<
  Exclude<AdminUserRole, "admin">,
  "list.relevantSeller" | "list.relevantHost" | "list.relevantSalesAgent"
> = {
  seller: "list.relevantSeller",
  host: "list.relevantHost",
  "sales-agent": "list.relevantSalesAgent",
};

type AdminUserListPageProps = {
  items: AdminUserListItem[];
  isLoading: boolean;
  error: boolean;
  onRetry: () => void;
  labels: AdminUserListLabels;
  detailPath: (item: AdminUserListItem) => string | null;
  onAddClick: () => void;
  onImpersonate?: (item: AdminUserListItem) => void;
  impersonatePending?: boolean;
  /** Super Admins may impersonate non-super staff; partners stay under USERS_IMPERSONATE. */
  canImpersonateStaff?: boolean;
  regionFilter: AdminRegionFilterValue;
  onRegionFilterChange: (value: AdminRegionFilterValue) => void;
};

function contactSortKey(item: AdminUserListItem): string {
  return (item.accountEmail || item.contactPhone || "").toLowerCase();
}

const ROLE_FILTER_OPTIONS: {
  value: RoleFilter;
  labelKey:
    | "list.filterRoleAll"
    | "list.filterRoleSeller"
    | "list.filterRoleHost"
    | "list.filterRoleSalesAgent"
    | "list.filterRoleAdmin";
}[] = [
  { value: "all", labelKey: "list.filterRoleAll" },
  { value: "seller", labelKey: "list.filterRoleSeller" },
  { value: "host", labelKey: "list.filterRoleHost" },
  { value: "sales-agent", labelKey: "list.filterRoleSalesAgent" },
  { value: "admin", labelKey: "list.filterRoleAdmin" },
];

const MAX_EMAIL_LOCAL_PART_LENGTH = 20;

function formatDisplayName(name: string): string {
  const atIndex = name.indexOf("@");
  if (atIndex === -1) return name;
  const localPart = name.slice(0, atIndex);
  if (localPart.length <= MAX_EMAIL_LOCAL_PART_LENGTH) return name;
  return `${localPart.slice(0, MAX_EMAIL_LOCAL_PART_LENGTH)}...${name.slice(atIndex)}`;
}

export function AdminUserListPage({
  items,
  isLoading,
  error,
  onRetry,
  labels,
  detailPath,
  onAddClick,
  onImpersonate,
  impersonatePending = false,
  canImpersonateStaff = false,
  regionFilter,
  onRegionFilterChange,
}: AdminUserListPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("adminUsers");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");

  type UserFilterOmit = "status" | "role" | "search" | null;

  const filterUsers = useCallback(
    (omit: UserFilterOmit = null) => {
      let result = items;
      if (omit !== "role" && roleFilter !== "all") {
        result = result.filter((item) => item.role === roleFilter);
      }
      if (omit !== "status") {
        result = result.filter((item) =>
          matchesStatusFilter(item.isActive, statusFilter),
        );
      }
      if (omit !== "search" && searchQuery.trim()) {
        result = result.filter((item) =>
          matchesTextQuery(
            searchQuery,
            [item.name, item.accountEmail, item.userId],
            { trim: false },
          ),
        );
      }
      return result;
    },
    [items, roleFilter, searchQuery, statusFilter],
  );

  const filtered = useMemo(() => filterUsers(null), [filterUsers]);

  const statusFilterCounts = useMemo(
    () => countByStatusFilter(filterUsers("status"), (item) => item.isActive),
    [filterUsers],
  );

  const roleFilterCounts = useMemo(() => {
    const base = filterUsers("role");
    return {
      all: base.length,
      seller: base.filter((item) => item.role === "seller").length,
      host: base.filter((item) => item.role === "host").length,
      "sales-agent": base.filter((item) => item.role === "sales-agent").length,
      admin: base.filter((item) => item.role === "admin").length,
    };
  }, [filterUsers]);

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    statusFilter !== "all" ||
    roleFilter !== "all" ||
    regionFilter !== null;

  const columns = useMemo<
    DataTableColumn<AdminUserListItem, SortColumn>[]
  >(() => {
    const cols: DataTableColumn<AdminUserListItem, SortColumn>[] = [
      {
        id: "name",
        header: labels.colName,
        sortKey: "name",
        sortKind: "text",
        getSortValue: (row) => row.name,
        defaultWidth: 280,
        alwaysVisible: true,
        cell: (item) => {
          const roleConfig = ROLE_ICON_CONFIG[item.role];
          const RoleIcon = roleConfig.icon;
          return (
            <span className="flex min-w-0 items-center gap-3">
              <span className="relative shrink-0">
                <span
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-lg text-xs font-semibold",
                    item.isActive
                      ? cn("shadow-xs", brandFillClass("primary"))
                      : "bg-surface-100 text-surface-500 ring-surface-200 ring-1 ring-inset",
                  )}
                  aria-hidden
                >
                  {getInitials(item.name)}
                </span>
                <span
                  className={cn(
                    "ring-surface-0 absolute -right-1 -bottom-1 flex h-4 w-4 items-center justify-center rounded ring-2",
                    roleConfig.className,
                  )}
                  aria-hidden
                >
                  <RoleIcon className="h-2.5 w-2.5" />
                </span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <DataTablePrimaryText className="min-w-0 truncate">
                    {formatDisplayName(item.name)}
                  </DataTablePrimaryText>
                  {item.isActive ? null : <AdminInactiveStatusDot />}
                </span>
                <span className="text-surface-500 flex items-center gap-1.5 text-xs">
                  {t(`roles.${item.role}`)}
                  {item.regionCode ? (
                    <>
                      <span aria-hidden>·</span>
                      <span className="font-mono tracking-wide">
                        {item.regionCode}
                      </span>
                    </>
                  ) : null}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: "contact",
        header: labels.colContact,
        sortKey: "contact",
        sortKind: "text",
        getSortValue: (row) => contactSortKey(row),
        defaultWidth: 280,
        cell: (item) => (
          <div className="text-surface-600 flex min-w-0 flex-col gap-0.5 text-[13px]">
            {item.accountEmail ? (
              <span className="flex min-w-0 items-center gap-1.5">
                <Mail className="text-surface-400 h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{item.accountEmail}</span>
              </span>
            ) : null}
            {item.contactPhone ? (
              <span className="flex items-center gap-1.5 tabular-nums">
                <Phone className="text-surface-400 h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{item.contactPhone}</span>
              </span>
            ) : null}
            {!item.accountEmail && !item.contactPhone ? (
              <span className="text-surface-400">—</span>
            ) : null}
          </div>
        ),
      },
      {
        id: "relevant",
        header: labels.colRelevant,
        sortKey: "relevant",
        sortKind: "number",
        getSortValue: (row) => row.relevantCount,
        defaultWidth: 150,
        cell: (item) =>
          item.role === "admin" ? null : (
            <span
              className={cn(
                "text-[13px] tabular-nums",
                item.relevantCount > 0 ? "text-surface-700" : "text-surface-400",
              )}
            >
              {t(RELEVANT_COUNT_KEY[item.role], { count: item.relevantCount })}
            </span>
          ),
      },
      {
        id: "balance",
        header: labels.colBalance,
        align: "right",
        sortKey: "balance",
        sortKind: "number",
        getSortValue: (row) => row.walletBalance,
        defaultWidth: 130,
        cell: (item) =>
          item.role === "admin" ? null : (
            <span
              className={cn(
                "font-medium tabular-nums",
                item.walletBalance > 0
                  ? "text-success-700"
                  : item.walletBalance < 0
                    ? "text-error-700"
                    : "text-surface-500",
              )}
            >
              {formatMoney(item.walletBalance)}
            </span>
          ),
      },
    ];

    if (onImpersonate) {
      cols.push(
        createActionsColumn(labels.colActions, {
          defaultWidth: 120,
          cell: (item) => {
            const canImpersonateTarget =
              item.isActive &&
              (item.role !== "admin"
                ? true
                : canImpersonateStaff && !item.isSuperAdmin);
            if (!canImpersonateTarget) {
              return null;
            }
            return (
              <HoverPopover content={labels.impersonate}>
                <button
                  type="button"
                  aria-label={labels.impersonate}
                  disabled={impersonatePending}
                  className={cn(
                    "inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-50",
                    item.role === "admin"
                      ? "text-accent-600 hover:bg-accent-50 hover:text-accent-700"
                      : "text-surface-500 hover:bg-surface-100 hover:text-surface-900",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    onImpersonate(item);
                  }}
                >
                  <UserCog className="h-4 w-4" />
                </button>
              </HoverPopover>
            );
          },
        }),
      );
    }

    return cols;
  }, [canImpersonateStaff, impersonatePending, labels, onImpersonate, t]);

  return (
    <PageFrame
      title={labels.title}
      description={labels.subtitle}
      actions={
        <Button
          onClick={onAddClick}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          {labels.addButton}
        </Button>
      }
    >
      <div className="-mx-1 overflow-x-auto px-1 [scrollbar-width:none]">
        <ToggleGroup
          variant="segmented-neutral"
          value={roleFilter}
          onChange={setRoleFilter}
          ariaLabel={t("list.filterRole")}
          options={ROLE_FILTER_OPTIONS.map((option) => ({
            value: option.value,
            icon: option.value === "all" ? Users : ROLE_ICON_CONFIG[option.value].icon,
            label: (
              <>
                {t(option.labelKey)}
                <span className="text-surface-400 text-xs tabular-nums">
                  {roleFilterCounts[option.value]}
                </span>
              </>
            ),
          }))}
        />
      </div>

      <ListToolbar
        search={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={labels.searchPlaceholder}
        searchAccessory={
          <div className="flex flex-wrap items-center gap-2">
            <AdminStatusFilter
              value={statusFilter}
              onChange={setStatusFilter}
              counts={statusFilterCounts}
              labels={{
                name: t("list.filterStatus"),
                all: t("list.filterAll"),
                active: t("list.filterActive"),
                inactive: t("list.filterInactive"),
              }}
            />
            <AdminRegionFilter
              value={regionFilter}
              onChange={onRegionFilterChange}
              valueKey="id"
            />
          </div>
        }
      />

      <DataTable
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={filtered}
        columns={columns}
        getRowKey={(item) => `${item.role}-${item.userId}`}
        defaultSortColumn="name"
        preferencesKey="admin-users"
        paginate
        resetToken={buildListResetToken([
          searchQuery,
          statusFilter,
          roleFilter,
          regionFilter ?? "",
        ])}
        loadState={
          isLoading
            ? { status: "loading" }
            : error
              ? {
                  status: "error",
                  message: labels.loadError,
                  retryLabel: labels.retry,
                  onRetry,
                }
              : undefined
        }
        emptyMessage={hasActiveFilters ? labels.emptySearch : labels.empty}
        onRowClick={(item) => {
          const path = detailPath(item);
          if (path) navigate(path);
        }}
        openRowLabel={(item) => labels.openRow(item.name)}
        isRowClickable={(item) => detailPath(item) !== null}
      />
    </PageFrame>
  );
}
